<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import {
  daysOptions,
  paymentOptions,
} from '../composables/salesPropertyFormOptions';
import SalesDetailFactList from './SalesDetailFactList.vue';

const props = defineProps({
  summary: { type: Boolean, default: false },
  canEdit: { type: Boolean, default: false },
  transaction: { type: Object, required: true },
});

const emit = defineEmits(['edit']);
const { t } = useI18n();
const profile = computed(() => props.transaction.profile || {});
const optionLabel = (options, value) =>
  options.find(option => option.value === String(value))?.label || value;
const paymentLabels = computed(() =>
  Array(profile.value.forma_pagamento)
    .map(value => optionLabel(paymentOptions(t), value))
    .join(', ')
);

const valueItems = computed(() => [
  {
    key: 'announced',
    label: t('SALES.DETAIL.PROPERTY_FIELDS.ANNOUNCED_VALUE'),
    value: profile.value.valorAnunciado,
  },
  {
    key: 'sale',
    label: props.summary
      ? t('SALES.DELIVERY.SUMMARY.PROPERTY.SALE_VALUE')
      : t('SALES.DETAIL.PROPERTY_FIELDS.SALE_VALUE'),
    value: profile.value.valorVenda || props.transaction.contracted_total,
  },
  {
    key: 'down-payment',
    label: props.summary
      ? t('SALES.DELIVERY.SUMMARY.PROPERTY.DOWN_PAYMENT')
      : t('SALES.DETAIL.PROPERTY_FIELDS.DOWN_PAYMENT'),
    value: profile.value.valorSinal,
  },
  {
    key: 'payment',
    label: t('SALES.DETAIL.PROPERTY_FIELDS.PAYMENT'),
    value: paymentLabels.value,
  },
  ...(props.summary
    ? [
        {
          key: 'payment-method',
          label: t('SALES.DELIVERY.SUMMARY.PROPERTY.PAYMENT_METHOD'),
          value:
            profile.value.meio_pagamento ||
            t('SALES.DELIVERY.SUMMARY.PROPERTY.UNKNOWN_PAYMENT_METHOD'),
        },
      ]
    : []),
]);

const deadlineItems = computed(() => [
  {
    key: 'days',
    label: props.summary
      ? t('SALES.DELIVERY.SUMMARY.PROPERTY.DEADLINE')
      : t('SALES.DETAIL.PROPERTY_FIELDS.DEADLINE'),
    value: profile.value.prazo,
  },
  ...(!props.summary
    ? [
        {
          key: 'day-type',
          label: t('SALES.DETAIL.PROPERTY_FIELDS.DAY_TYPE'),
          value: optionLabel(daysOptions(t), profile.value.tipo_dias),
        },
      ]
    : []),
  {
    key: 'penalty',
    label: props.summary
      ? t('SALES.DELIVERY.SUMMARY.PROPERTY.PENALTY')
      : t('SALES.DETAIL.PROPERTY_FIELDS.PENALTY'),
    value:
      profile.value.valoMulta ||
      (props.summary
        ? t('SALES.DELIVERY.SUMMARY.PROPERTY.ZERO_DAILY_PENALTY')
        : null),
  },
]);
</script>

<template>
  <div class="grid gap-4 xl:grid-cols-2">
    <LaisCard
      :title="
        summary
          ? t('SALES.DELIVERY.SUMMARY.PROPERTY.VALUES_TITLE')
          : t('SALES.DETAIL.PROPERTY_SECTIONS.VALUES')
      "
      prefix-icon="i-lucide-circle-dollar-sign"
    >
      <SalesDetailFactList :items="valueItems" />
      <div v-if="canEdit && !summary" class="mt-4 flex justify-end">
        <LaisPrimaryButton
          data-test="sales-detail-edit-values"
          icon="i-lucide-pencil"
          variant="link"
          size="sm"
          @click="emit('edit', 'values')"
        >
          {{ t('SALES_PROPERTY.ACTIONS.EDIT') }}
        </LaisPrimaryButton>
      </div>
    </LaisCard>
    <LaisCard
      :title="
        summary
          ? t('SALES.DELIVERY.SUMMARY.PROPERTY.DEADLINE_TITLE')
          : t('SALES.DETAIL.PROPERTY_SECTIONS.DEADLINE')
      "
      prefix-icon="i-lucide-calendar-clock"
    >
      <SalesDetailFactList :items="deadlineItems" />
      <div v-if="canEdit && !summary" class="mt-4 flex justify-end">
        <LaisPrimaryButton
          data-test="sales-detail-edit-deadline"
          icon="i-lucide-pencil"
          variant="link"
          size="sm"
          @click="emit('edit', 'deadline')"
        >
          {{ t('SALES_PROPERTY.ACTIONS.EDIT') }}
        </LaisPrimaryButton>
      </div>
    </LaisCard>
  </div>
</template>
